import { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import { Replayer } from "rrweb";
import type { eventWithTime } from "rrweb";
import "rrweb/dist/style.css";

/**
 * Real rrweb session-replay playback (capability #16) for WEB sessions.
 *
 * Ported from builder-ui's working analytics ReplaysPanel: it drives rrweb's
 * `Replayer` class DIRECTLY — which renders the recorded DOM snapshots into an
 * iframe and reliably runs its mount lifecycle — instead of the `rrweb-player`
 * Svelte wrapper. rrweb-player@2.0.1's bundled Svelte build does NOT run its
 * mount lifecycle under `new rrwebPlayer(...)`, so it silently rendered an empty
 * frame; worse, it failed WITHOUT throwing, so the old onUnsupported fallback
 * never fired. Replayer mounts cleanly and lets us surface onUnsupported honestly.
 *
 * Contract: { events; onUnsupported? }. The host falls back to the wireframe
 * preview for mobile sessions, streams too short to replay (<2 events), or any
 * recording the Replayer cannot construct/play.
 */

/** mm:ss from a millisecond offset (Replayer reports time in ms). */
function clockMs(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** The first Meta (type 4) event carries the recorded viewport, used to scale
 *  the replay to fit the panel width. */
function recordedViewport(events: eventWithTime[]): { width: number; height: number } {
  for (const e of events) {
    const ev = e as unknown as { type?: number; data?: { width?: number; height?: number } };
    if (ev.type === 4 && ev.data?.width && ev.data?.height) {
      return { width: ev.data.width, height: ev.data.height };
    }
  }
  return { width: 1024, height: 768 };
}

const SPEEDS = [1, 2, 4, 8] as const;

export function RrwebPlayer({
  events,
  onUnsupported,
}: {
  events: unknown[];
  onUnsupported?: () => void;
}) {
  const boxRef = useRef<HTMLDivElement | null>(null); // sized, clipped viewport
  const rootRef = useRef<HTMLDivElement | null>(null); // Replayer mounts its iframe here
  const replayerRef = useRef<Replayer | null>(null);
  // Keep the latest onUnsupported without re-running the mount effect (the host
  // passes an inline arrow whose identity changes every render).
  const onUnsupportedRef = useRef(onUnsupported);
  onUnsupportedRef.current = onUnsupported;

  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [total, setTotal] = useState(0);
  const [speed, setSpeed] = useState<number>(1);

  useEffect(() => {
    // Degenerate input: rrweb needs a full snapshot + at least one more event.
    // Surface the fallback (in an effect, never during render) and bail.
    if (events.length < 2) {
      onUnsupportedRef.current?.();
      return;
    }
    const box = boxRef.current;
    const root = rootRef.current;
    if (!box || !root) return;
    root.innerHTML = "";

    let replayer: Replayer;
    try {
      replayer = new Replayer(events as eventWithTime[], {
        root,
        skipInactive: false,
        showWarning: false,
        mouseTail: false,
        speed,
      });
    } catch {
      root.innerHTML = "";
      onUnsupportedRef.current?.(); // construction failed → swap to wireframe
      return;
    }
    replayerRef.current = replayer;

    let totalTime = 0;
    try {
      totalTime = replayer.getMetaData().totalTime;
    } catch {
      /* leave totalTime at 0; controls still render */
    }
    setTotal(totalTime);
    setCur(0);
    setPlaying(false);

    // Scale the recording's viewport down to the panel width.
    const vp = recordedViewport(events as eventWithTime[]);
    const fit = () => {
      const wrap = root.querySelector(".replayer-wrapper") as HTMLElement | null;
      const scale = vp.width ? Math.min(1, box.clientWidth / vp.width) : 1;
      if (wrap) {
        wrap.style.transform = `scale(${scale})`;
        wrap.style.transformOrigin = "top left";
      }
      box.style.height = `${Math.round(vp.height * scale)}px`;
    };
    fit();
    // Recompute scale once layout settles and on any container resize. (The
    // synchronous fit() can run before the grid track is constrained.)
    const ro = new ResizeObserver(() => fit());
    ro.observe(box);

    let raf = 0;
    const tick = () => {
      const r = replayerRef.current;
      if (r) setCur(Math.min(r.getCurrentTime(), totalTime));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      try {
        replayer.pause();
      } catch {
        /* ignore */
      }
      replayerRef.current = null;
      root.innerHTML = "";
    };
    // Re-init only when the underlying recording changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  function toggle() {
    const r = replayerRef.current;
    if (!r) return;
    try {
      if (playing) {
        r.pause();
        setPlaying(false);
      } else {
        r.play(cur >= total ? 0 : cur); // restart from 0 when at the end
        setPlaying(true);
      }
    } catch {
      onUnsupportedRef.current?.(); // playback threw → swap to wireframe
    }
  }

  function seek(ms: number) {
    const r = replayerRef.current;
    if (!r) return;
    try {
      if (playing) r.play(ms);
      else r.pause(ms);
      setCur(ms);
    } catch {
      onUnsupportedRef.current?.();
    }
  }

  function changeSpeed(s: number) {
    setSpeed(s);
    const r = replayerRef.current;
    if (!r) return;
    try {
      r.setConfig({ speed: s });
    } catch {
      /* speed is best-effort; ignore */
    }
  }

  // Effect already fired onUnsupported for degenerate input; render nothing so
  // the host can swap in the wireframe without a flash of empty player.
  if (events.length < 2) return null;

  return (
    <div className="space-y-3">
      <div
        ref={boxRef}
        className="dl-rrweb relative overflow-hidden rounded-card border border-hairline bg-canvas"
      >
        <div ref={rootRef} />
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={toggle}
          aria-label={playing ? "Pause" : "Play"}
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-accent text-white transition-colors hover:bg-accent-hover"
        >
          {playing ? (
            <Pause className="h-4 w-4" strokeWidth={2} />
          ) : (
            <Play className="h-4 w-4 translate-x-px" strokeWidth={2} />
          )}
        </button>
        <span className="shrink-0 font-mono text-[11px] text-muted tabular-nums">
          {clockMs(cur)} / {clockMs(total)}
        </span>
        <input
          type="range"
          min={0}
          max={total || 0}
          value={Math.min(cur, total || 0)}
          onChange={(e) => seek(Number(e.target.value))}
          aria-label="Scrub replay"
          className="h-9 flex-1 cursor-pointer appearance-none bg-transparent"
          style={{ accentColor: "var(--accent)" }}
        />
        <div className="flex shrink-0 gap-1">
          {SPEEDS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => changeSpeed(s)}
              className={
                "rounded-md px-2 py-1 font-mono text-[10px] transition-colors " +
                (speed === s
                  ? "bg-accent text-white"
                  : "bg-paper-2 text-muted hover:text-ink-strong dark:bg-white/5")
              }
            >
              {s}×
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
